import { useState, useEffect, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import { BASE_URL } from "../api";

// ─── API Layer ────────────────────────────────────────────────────────────────

const API_BASE = `${BASE_URL}/api`;

const getAuthHeader = (token) => {
  if (!token) return {};

  // JWT token
  if (token.split(".").length === 3) {
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  // DRF token
  return {
    Authorization: `Token ${token}`,
  };
};

const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem("token");

  if (!token) {
    throw new Error("User not logged in");
  }

  const config = {
    ...options,

    headers: {
      ...getAuthHeader(token),

      ...(options.body instanceof FormData
        ? {}
        : {
            "Content-Type": "application/json",
          }),

      ...(options.headers || {}),
    },
  };

  const res = await fetch(url, config);

  if (res.status === 401) {
    localStorage.removeItem("token");

    throw new Error("Session expired. Please login again.");
  }

  if (!res.ok) {
    let msg = "Request failed";

    try {
      const err = await res.json();
      msg = err.detail || JSON.stringify(err);
    } catch {
      msg = await res.text();
    }

    throw new Error(msg);
  }

  return res.status === 204 ? null : res.json();
};

const api = {
  getCamps: () =>
    apiRequest(`${API_BASE}/billing-camps/`),

  previewBill: (campId) =>
    apiRequest(`${API_BASE}/preview-bill/${campId}/`),
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const inr = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const STATUS_COLORS = {
  Active: { bg: "#e6f4ea", text: "#1e7e34" },
  Completed: { bg: "#e8f0fe", text: "#1a56db" },
  Inactive: { bg: "#fef3c7", text: "#92400e" },
};

const CHART_COLORS = ["#1a56db", "#059669", "#d97706", "#7c3aed", "#dc2626", "#0891b2", "#be185d"];

// ─── Sub-components ───────────────────────────────────────────────────────────

function MetricCard({
  icon,
  label,
  value,
  sub,
  accent,
}) {
  return (
    <div
      style={{
        ...styles.metricCard,
        borderTop: `4px solid ${accent}`,
      }}
    >
      <div style={styles.metricTop}>
        <span style={styles.metricIcon}>
          {icon}
        </span>

        <span
          style={{
            ...styles.metricDot,
            background: accent,
          }}
        />
      </div>

      <p style={styles.metricLabel}>
        {label}
      </p>

      <p style={styles.metricValue}>
        {value}
      </p>

      {sub && (
        <p style={styles.metricSub}>
          {sub}
        </p>
      )}
    </div>
  );
}

function Badge({ children, type = "default" }) {
  const typeMap = {
    active: { background: "#e6f4ea", color: "#1e7e34" },
    pending: { background: "#fef3c7", color: "#92400e" },
    info: { background: "#e8f0fe", color: "#1a56db" },
    default: { background: "#f3f4f6", color: "#6b7280" },
  };
  return (
    <span style={{ ...styles.badge, ...typeMap[type] }}>
      {children}
    </span>
  );
}

function SectionTitle({ children }) {
  return <p style={styles.sectionTitle}>{children}</p>;
}

function Card({ children, style, darkMode }) {
  return (
    <div
      style={{
        ...styles.card,
        ...(darkMode ? styles.glassDark : styles.glassLight),
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div style={styles.spinner}>
      <div style={styles.spinnerDot} />
      <span style={{ color: "#6b7280", fontSize: 13 }}>Loading...</span>
    </div>
  );
}

function EmptyState({ message }) {
  return <p style={{ color: "#9ca3af", fontSize: 13, padding: "1rem 0" }}>{message}</p>;
}

// ─── Camp List ────────────────────────────────────────────────────────────────

function CampListRow({ camp, selected, onClick }) {
  const isActive = getCampStatus(camp) === "Active";
  return (
    <div
      onClick={() => onClick(camp)}
      style={{
        ...styles.campRow,
        background: selected
          ? "rgba(59,130,246,0.12)"
          : "rgba(255,255,255,0.03)",

        borderLeft: selected
          ? "3px solid #3b82f6"
          : "3px solid transparent",

        boxShadow: selected
          ? "0 0 0 1px rgba(59,130,246,0.35), 0 10px 30px rgba(59,130,246,0.15)"
          : "none",

        transform: selected ? "translateY(-1px)" : "translateY(0px)",

        cursor: "pointer",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={styles.campName}>
          {camp.branch_name || camp.branch_code}
        </p>
        <p style={styles.campMeta}>
          {camp.client_name} &bull; {camp.location || "—"}, {camp.state || "—"}
        </p>
        <p style={styles.campMeta}>
          {camp.start_date} → {camp.end_date}
        </p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
        <Badge type={isActive ? "active" : "info"}>{getCampStatus(camp)}</Badge>
        {camp.ready_to_go && <Badge type="active">Ready ✓</Badge>}
      </div>
    </div>
  );
}

// ─── Billing Preview ──────────────────────────────────────────────────────────

function BillingPreview({ campId, darkMode }) {
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!campId) return;

    setLoading(true);
    setError(null);

    api
      .previewBill(campId)
      .then((data) => setBill(data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [campId]);

  if (!campId) {
    return (
      <EmptyState message="Select a camp to preview its billing." />
    );
  }

  if (loading) return <Spinner />;

  if (error) {
    return <div style={styles.errorBox}>⚠ {error}</div>;
  }

  if (!bill) return null;

  const {
    preview = [],
    summary = {},
    total_amount = 0,
  } = bill;

  const isMedibuddy =
    summary.billing_mode === "MEDIBUDDY";

  // ───────────────── MEDIBUDDY PIE CHART ─────────────────

  const pieData = isMedibuddy
    ? preview.map((item) => ({
        name: item.component,
        value: item.amount,
      }))
    : [
        {
          name: "Actual cases",
          value: summary.actual_cases || 0,
        },
        {
          name: "MG minimum",
          value: Math.max(
            0,
            (summary.billable_cases || 0) -
              (summary.service_mg_extra || 0)
          ),
        },
        {
          name: "Extra (MG+)",
          value: summary.service_mg_extra || 0,
        },
      ].filter((d) => d.value > 0);

  // ───────────────── SUMMARY ROWS ─────────────────

  const summaryRows = isMedibuddy
    ? [
        [
          "Billing mode",
          summary.billing_mode || "—",
        ],
        [
          "Actual cases",
          fmt(summary.actual_cases),
        ],
        [
          "Camp days",
          fmt(summary.camp_days),
        ],
        [
          "Subtotal",
          inr(summary.subtotal),
        ],
        [
          "Final total",
          inr(summary.final_total),
        ],
      ]
    : [
        [
          "Billing mode",
          summary.billing_mode || "—",
        ],
        [
          "Actual cases",
          fmt(summary.actual_cases),
        ],
        [
          "Billable cases",
          fmt(summary.billable_cases),
        ],
        [
          "MG extra cases",
          fmt(summary.service_mg_extra),
        ],
        [
          "Subtotal",
          inr(summary.subtotal),
        ],
        [
          "Extra charges",
          inr(summary.extra_charges),
        ],
      ];

  return (
    <div>
      {/* ───────── SUMMARY + PIE ───────── */}

      <div style={styles.twoCol}>
        <Card darkMode={darkMode}>
          <SectionTitle>
            Billing Summary
          </SectionTitle>

          {summaryRows.map(([k, v]) => (
            <div
              key={k}
              style={styles.summaryRow}
            >
              <span style={{ color: "#6b7280" }}>
                {k}
              </span>

              <span style={{ fontWeight: 600 }}>
                {v}
              </span>
            </div>
          ))}

          <div
            style={{
              ...styles.summaryRow,
              borderTop: "1px solid #e5e7eb",
              marginTop: 10,
              paddingTop: 14,
            }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: 15,
              }}
            >
              Total Amount
            </span>

            <span
              style={{
                fontWeight: 800,
                fontSize: 18,
                color: "#059669",
              }}
            >
              {inr(total_amount)}
            </span>
          </div>
        </Card>

        {/* PIE CHART */}

        <Card darkMode={darkMode}>
          <SectionTitle>
            {isMedibuddy
              ? "Charge Breakdown"
              : "Case Breakdown"}
          </SectionTitle>

          <ResponsiveContainer
            width="100%"
            height={220}
          >
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                dataKey="value"
              >
                {pieData.map((_, i) => (
                  <Cell
                    key={i}
                    fill={
                      CHART_COLORS[
                        i % CHART_COLORS.length
                      ]
                    }
                  />
                ))}
              </Pie>

              <Legend
                iconType="circle"
                wrapperStyle={{
                  fontSize: 12,
                }}
              />

              <Tooltip
                formatter={(v) => inr(v)}
              />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* ───────── TABLE ───────── */}

      <Card darkMode={darkMode}>
        <SectionTitle>
          {isMedibuddy
            ? "Billing Components"
            : "Service Line Detail"}
        </SectionTitle>

        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {isMedibuddy ? (
                  <>
                    <th style={styles.th}>
                      Component
                    </th>

                    <th style={styles.th}>
                      Units
                    </th>

                    <th style={styles.th}>
                      Rate
                    </th>

                    <th style={styles.th}>
                      Amount
                    </th>
                  </>
                ) : (
                  <>
                    <th style={styles.th}>
                      Service
                    </th>

                    <th style={styles.th}>
                      Actual Cases
                    </th>

                    <th style={styles.th}>
                      Billable Cases
                    </th>

                    <th style={styles.th}>
                      Rate
                    </th>

                    <th style={styles.th}>
                      Amount
                    </th>

                    <th style={styles.th}>
                      Source
                    </th>
                  </>
                )}
              </tr>
            </thead>

            <tbody>
              {preview.map((row, i) => (
                <tr
                  key={i}
                  style={{
                    background:
                      i % 2 === 0
                        ? "rgba(255,255,255,0.02)"
                        : "rgba(255,255,255,0.01)",
                  }}
                >
                  {isMedibuddy ? (
                    <>
                      <td
                        style={{
                          ...styles.td,
                          fontWeight: 600,
                        }}
                      >
                        {row.component}
                      </td>

                      <td style={styles.tdNum}>
                        {fmt(row.units)}
                      </td>

                      <td style={styles.tdNum}>
                        {inr(row.rate)}
                      </td>

                      <td
                        style={{
                          ...styles.tdNum,
                          color: "#059669",
                          fontWeight: 700,
                        }}
                      >
                        {inr(row.amount)}
                      </td>
                    </>
                  ) : (
                    <>
                      <td
                        style={{
                          ...styles.td,
                          fontWeight: 500,
                        }}
                      >
                        {row.service_name}
                      </td>

                      <td style={styles.tdNum}>
                        {fmt(row.actual_cases)}
                      </td>

                      <td style={styles.tdNum}>
                        {fmt(row.billable_cases)}
                      </td>

                      <td style={styles.tdNum}>
                        {inr(row.rate)}
                      </td>

                      <td
                        style={{
                          ...styles.tdNum,
                          color: "#059669",
                          fontWeight: 600,
                        }}
                      >
                        {inr(row.amount)}
                      </td>

                      <td style={styles.td}>
                        <Badge type="info">
                          {row.pricing_source}
                        </Badge>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Client Distribution Chart ────────────────────────────────────────────────

function ClientChart({ camps }) {
  const counts = {};
  camps.forEach((c) => { counts[c.client_name] = (counts[c.client_name] || 0) + 1; });
  const data = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, count]) => ({ name, count }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 40, left: 0 }}>
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11, fill: "#6b7280" }}
          angle={-25}
          textAnchor="end"
          interval={0}
        />
        <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} allowDecimals={false} />
        <Tooltip cursor={{ fill: "#f3f4f6" }} contentStyle={{ fontSize: 12 }} />
        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
          {data.map((_, i) => (
            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const getCampStatus = (camp) => {
    if (!camp.start_date || !camp.end_date) {
      return "Inactive";
    }

    const today = new Date();

    today.setHours(0, 0, 0, 0);

    const start = new Date(camp.start_date);
    const end = new Date(camp.end_date);

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    if (today < start) {
      return "Upcoming";
    }

    if (today > end) {
      return "Completed";
    }

    return "Active";
  };

export default function CampBillingDashboard() {
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [darkMode, setDarkMode] = useState(false);

  const loadCamps = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .getCamps()
      .then((data) => setCamps(Array.isArray(data) ? data : data.results || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadCamps(); }, [loadCamps]);

  const filtered = camps.filter((c) => {
    const matchSearch =
      !search ||
      [c.client_name, c.branch_name, c.branch_code, c.location, c.state]
        .some((v) => v && v.toLowerCase().includes(search.toLowerCase()));
    const matchStatus =
      statusFilter === "All" ||
      getCampStatus(c) === statusFilter;
    return matchSearch && matchStatus;
  });

  const today = new Date();

    today.setHours(0, 0, 0, 0);

    const activeCount = camps.filter(
      (camp) => getCampStatus(camp) === "Active"
    ).length;
  const readyCount = camps.filter((c) => c.ready_to_go).length;
  const clientCount = new Set(camps.map((c) => c.client)).size;

  return (
    <div
      style={{
        ...styles.root,
        ...(darkMode ? styles.darkTheme : styles.lightTheme),
      }}
    >
      {/* ───────────────────────── HEADER ───────────────────────── */}

      <div style={styles.topBar}>
        <div>
          <h1 style={styles.h1}>Camp Billing Dashboard</h1>

          <p style={styles.headerSub}>
            Monitor camps, billing previews & operational status
          </p>
        </div>

        <div style={styles.headerActions}>
          <button
            style={{
              ...styles.actionBtn,
              ...(darkMode ? styles.darkButton : styles.lightButton),
            }}
            onClick={loadCamps}
          >
            ↻ Refresh
          </button>

          <button
            style={{
              ...styles.actionBtn,
              ...(darkMode ? styles.darkButton : styles.lightButton),
            }}
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? "☀ Light" : "🌙 Dark"}
          </button>
        </div>
      </div>

      {/* ───────────────────────── KPI CARDS ───────────────────────── */}

      <div style={styles.metricGrid}>
        <MetricCard
          icon="🏕"
          label="Total Camps"
          value={camps.length}
          accent="#2563eb"
          sub="Overall registered camps"
        />

        <MetricCard
          icon="🟢"
          label="Active Camps"
          value={activeCount}
          accent="#059669"
          sub="Currently operational"
        />

        <MetricCard
          icon="✅"
          label="Ready To Go"
          value={readyCount}
          accent="#d97706"
          sub="Billing ready camps"
        />

        <MetricCard
          icon="🏢"
          label="Clients"
          value={clientCount}
          accent="#7c3aed"
          sub="Unique organizations"
        />
      </div>

      {/* ───────────────────────── CLIENT CHART ───────────────────────── */}

      <Card darkMode={darkMode} style={{ marginBottom: 24 }}>
        <div style={styles.sectionHeader}>
          <SectionTitle>Client Distribution</SectionTitle>

          <span style={styles.sectionHint}>
            Top clients by camp count
          </span>
        </div>

        {camps.length > 0 ? (
          <ClientChart camps={camps} />
        ) : (
          <Spinner />
        )}
      </Card>

      {/* ───────────────────────── MAIN GRID ───────────────────────── */}
      <div style={styles.mainGrid}>
        
        {/* ───────────────── LEFT PANEL ───────────────── */}

        <div>
          <Card darkMode={darkMode} style={{ marginBottom: 0 }}>
            <div style={styles.sectionHeader}>
              <SectionTitle>All Camps</SectionTitle>

              <Badge type="info">
                {filtered.length} Results
              </Badge>
            </div>

            {/* Search & Filters */}

            <div style={styles.toolbar}>
              <input
                style={styles.searchInput}
                placeholder="Search camps, locations, clients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <select
                style={styles.selectInput}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {["All", "Active", "Upcoming", "Completed"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>

            {loading && <Spinner />}

            {error && (
              <div style={styles.errorBox}>
                ⚠ {error}
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <EmptyState message="No camps found." />
            )}

            <div style={styles.campList}>
              {filtered.map((camp) => (
                <div
                  key={camp.id}
                  onClick={() => setSelectedCamp(camp)}
                  style={{
                    ...styles.modernCampCard,

                    ...(selectedCamp?.id === camp.id
                      ? styles.selectedCampCard
                      : {}),
                  }}
                >
                  <div style={styles.campTop}>
                    <div>
                      <p style={styles.campName}>
                        {camp.branch_name || camp.branch_code}
                      </p>

                      <p style={styles.campClient}>
                        {camp.client_name}
                      </p>
                    </div>

                    <Badge
                      type={
                        getCampStatus(camp) === "Active"
                          ? "active"
                          : "info"
                      }
                    >
                      {getCampStatus(camp)}
                    </Badge>
                  </div>

                  <div style={styles.campDetails}>
                    <span>📍 {camp.location || "N/A"}</span>

                    <span>🗓 {camp.start_date}</span>
                  </div>

                  {camp.ready_to_go && (
                    <div style={{ marginTop: 10 }}>
                      <Badge type="active">
                        Ready for Billing ✓
                      </Badge>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* ───────────────── RIGHT PANEL ───────────────── */}

        <div>
          <Card darkMode={darkMode}>
            <div style={styles.sectionHeader}>
              <SectionTitle>
                Billing Preview
              </SectionTitle>

              {selectedCamp && (
                <Badge type="info">
                  {selectedCamp.branch_name}
                </Badge>
              )}
            </div>

            <BillingPreview
              campId={selectedCamp?.id}
              darkMode={darkMode}
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  root: {
    fontFamily: "'Inter', 'DM Sans', sans-serif",
    minHeight: "100vh",
    padding: "1.5rem",
    transition: "all 0.3s ease",
  },

  darkTheme: {
    background:
      "radial-gradient(circle at top left, #0f172a 0%, #020617 45%, #000814 100%)",
    color: "#f8fafc",
  },

  lightTheme: {
    background:
      "linear-gradient(to bottom right, #f8fafc, #eef2ff, #f1f5f9)",
    color: "#0f172a",
  },

  glassDark: {
    background: "rgba(15, 23, 42, 0.72)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
  },

  glassLight: {
    background: "rgba(255,255,255,0.88)",

    border: "1px solid rgba(226,232,240,0.8)",

    boxShadow: "0 10px 30px rgba(15,23,42,0.06)",

    backdropFilter: "blur(8px)",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "2rem",
  },

  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 32,
  },

  headerActions: {
    display: "flex",
    gap: 12,
  },

  actionBtn: {
    padding: "10px 18px",
    borderRadius: 14,
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
    transition: "all 0.2s ease",
  },

  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 18,
  },

  sectionHint: {
    fontSize: 12,
    color: "#6b7280",
  },

  toolbar: {
    display: "flex",
    gap: 10,
    marginBottom: 18,
  },

  campList: {
    maxHeight: 620,
    overflowY: "auto",
    paddingRight: 4,
  },

  modernCampCard: {
    padding: 18,
    borderRadius: 20,
    marginBottom: 14,
    cursor: "pointer",

    background: "rgba(255,255,255,0.7)",

    border: "1px solid rgba(226,232,240,0.9)",

    transition: "all 0.25s ease",

    boxShadow: "0 4px 14px rgba(15,23,42,0.04)",
  },

  selectedCampCard: {
    border: "1px solid #3b82f6",

    boxShadow:
      "0 10px 30px rgba(59,130,246,0.15)",

    transform: "translateY(-2px)",
  },

  campTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },

  campClient: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 4,
  },

  campDetails: {
    display: "flex",
    gap: 16,
    marginTop: 14,
    fontSize: 12,
    color: "#64748b",
  },

  metricTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },

  metricIcon: {
    fontSize: 24,
  },

  metricDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
  },

  metricCard: {
    borderRadius: 24,
    padding: 22,

    background: "rgba(255,255,255,0.8)",

    border: "1px solid rgba(226,232,240,0.8)",

    boxShadow: "0 10px 30px rgba(15,23,42,0.06)",

    transition: "all 0.25s ease",
  },

  mainGrid: {
    display: "grid",
    gridTemplateColumns: "0.95fr 1.5fr",
    gap: 24,
    alignItems: "start",
  },



  






  h1: {
    fontSize: 34,
    fontWeight: 700,
    margin: 0,
    letterSpacing: "-1px",
  },

  headerSub: {
    fontSize: 14,
    opacity: 0.7,
    marginTop: 6,
  },

  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 18,
    marginBottom: "1.5rem",
  },

  // metricCard: {
  //   borderRadius: 24,
  //   padding: "1.4rem",
  //   background: "rgba(255,255,255,0.04)",
  //   backdropFilter: "blur(12px)",
  //   border: "1px solid rgba(255,255,255,0.05)",
  //   transition: "all 0.25s ease",
  // },

  metricLabel: {
    fontSize: 13,
    opacity: 0.7,
    marginBottom: 10,
  },

  metricValue: {
    fontSize: 34,
    fontWeight: 700,
    margin: 0,
  },

  metricSub: {
    fontSize: 12,
    opacity: 0.6,
    marginTop: 8,
  },

  card: {
    borderRadius: 28,
    padding: "1.4rem",
    marginBottom: "1.4rem",
    transition: "all 0.3s ease",
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    opacity: 0.7,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    marginBottom: "1rem",
  },

  // mainGrid: {
  //   display: "grid",
  //   gridTemplateColumns: "0.95fr 1.45fr",
  //   gap: "1.5rem",
  //   alignItems: "start",
  // },

  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1rem",
    marginBottom: "1rem",
  },

  campRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "14px",
    borderRadius: 18,
    marginBottom: 10,
    transition: "all 0.25s ease",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.04)",
  },

  campName: {
    fontSize: 14,
    fontWeight: 600,
    margin: 0,
  },

  campMeta: {
    fontSize: 12,
    opacity: 0.65,
    marginTop: 4,
  },

  searchInput: {
    flex: 1,

    padding: "13px 16px",

    borderRadius: 14,

    border: "1px solid #dbe3ee",

    background: "#fff",

    color: "#111827",

    fontSize: 13,

    outline: "none",

    transition: "all 0.2s ease",
  },

  selectInput: {
    padding: "13px 16px",

    borderRadius: 14,

    border: "1px solid #dbe3ee",

    background: "#fff",

    color: "#111827",

    fontSize: 13,

    outline: "none",

    cursor: "pointer",
  },

  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    fontSize: 14,
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },

  th: {
    textAlign: "left",
    padding: "14px 12px",
    fontSize: 11,
    fontWeight: 700,
    opacity: 0.7,
    textTransform: "uppercase",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },

  td: {
    padding: "14px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
  },

  tdNum: {
    padding: "14px 12px",
    textAlign: "right",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    backdropFilter: "blur(8px)",
  },

  refreshBtn: {
    padding: "10px 16px",
    borderRadius: 14,
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
    transition: "all 0.2s ease",
  },

  themeBtn: {
    padding: "10px 16px",
    borderRadius: 14,
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
  },

  darkButton: {
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
  },

  lightButton: {
    background: "#fff",
    color: "#111827",
    boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
  },

  spinner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "2rem",
  },

  spinnerDot: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "2px solid rgba(255,255,255,0.2)",
    borderTopColor: "#3b82f6",
    animation: "spin 0.8s linear infinite",
  },

  errorBox: {
    padding: "14px",
    borderRadius: 16,
    background: "rgba(239,68,68,0.15)",
    border: "1px solid rgba(239,68,68,0.2)",
    color: "#fca5a5",
    fontSize: 13,
  },
};